import { Action } from 'kbar';

import { PAYMENT_PAGE } from 'src/constants';
import { PaymentTab, paymentTabs } from 'src/routes/payments/paymentTabs';
import type { useRouteAccess } from 'src/routes/routeAccess';

import { isNavItem, NavItem, NavSection } from './SidebarNav';

const NAVIGATION_SECTION = 'Navigation';

export const PAYMENTS_NAV_ITEM_ID = 'sidebar-payments';

/**
 * The payments tabs the command bar offers, by the ids the rest of it keys off,
 * so they stay stable even as the tabs are renamed or reordered. Overview is
 * left out because the Payments nav item itself already navigates there.
 */
const paymentTabActionIds: Partial<Record<PaymentTab, string>> = {
  [PaymentTab.Invoices]: 'sidebar-invoices',
  [PaymentTab.Subscriptions]: 'sidebar-subscriptions',
  [PaymentTab.Store]: 'sidebar-store',
  [PaymentTab.PaymentLinks]: 'sidebar-payment-links',
  [PaymentTab.Services]: 'sidebar-products',
};

type SidebarCommandBarRouteAccess = Pick<
  ReturnType<typeof useRouteAccess>['routeAccess'],
  | 'allowPaymentsPage'
  | 'allowInvoices'
  | 'allowProductStore'
  | 'allowSubscriptionPage'
>;

/** Each tab is gated exactly as the page it opens is. */
const canAccessPaymentTab = (
  tab: PaymentTab,
  routeAccess: SidebarCommandBarRouteAccess,
): boolean => {
  if (!routeAccess.allowSubscriptionPage) return false;

  switch (tab) {
    case PaymentTab.Invoices:
      return routeAccess.allowInvoices;
    case PaymentTab.Store:
      return routeAccess.allowProductStore;
    default:
      return routeAccess.allowPaymentsPage;
  }
};

/**
 * Flattens the sidebar sections into the nav items a user can actually reach.
 * Folders contribute their children as well as themselves.
 */
const getAccessibleNavItems = (sections: Array<NavSection>): Array<NavItem> =>
  sections
    .map((section) => {
      const items = section.navItems;
      if (items.some((item) => item.subItems)) {
        return items.flatMap((item) =>
          item.subItems ? [item, ...item.subItems] : item,
        );
      }
      return items;
    })
    .flat(Infinity)
    .filter(
      (item): item is NavItem =>
        isNavItem(item) &&
        Boolean(item.canAccess) &&
        item.htmlId.includes('sidebar') &&
        !item.isHidden,
    );

/**
 * Turns the sidebar into command bar navigation actions. Access is inherited
 * from the sidebar itself: an app a user cannot open is never registered, so a
 * staff member without billing access gets no payments commands.
 */
export const getSidebarCommandBarActions = ({
  sections,
  isClient,
  routeAccess,
  navigate,
  goToSettings,
}: {
  sections: Array<NavSection>;
  isClient: boolean;
  routeAccess: SidebarCommandBarRouteAccess;
  navigate: (path: string) => void;
  goToSettings: () => void;
}): Array<Action> => {
  const navItems = getAccessibleNavItems(sections);

  const actions: Action[] = [
    ...navItems.map(({ htmlId, label, path, disabled }) => ({
      section: NAVIGATION_SECTION,
      id: htmlId,
      name: `Go to ${label}`,
      // Apps can be renamed per workspace, so keep the payments app findable
      // under its product name whatever this workspace calls it.
      keywords:
        htmlId === PAYMENTS_NAV_ITEM_ID
          ? PAYMENT_PAGE.label.toLowerCase()
          : undefined,
      disabled,
      perform: () => (!disabled ? navigate(path) : null), // Disabled items can not be navigated to.
    })),
    // Adding sidebar section manually because it's not part of main sidebar nav items
    {
      section: NAVIGATION_SECTION,
      id: 'internal-settings',
      name: 'Go to Settings',
      perform: () => goToSettings(),
    },
  ];

  // The payments tabs are not sidebar items — they are consolidated under the
  // Payments app — so each one is registered here to stay reachable by name.
  // Hiding the app from the sidebar drops its own command but keeps the tabs,
  // which is how the tabs have always behaved.
  const canAccessPayments = sections
    .flatMap((section) => section.navItems)
    .some((item) => item.htmlId === PAYMENTS_NAV_ITEM_ID && item.canAccess);
  if (!isClient && canAccessPayments) {
    actions.push(
      ...paymentTabs.flatMap((tab): Array<Action> => {
        const id = paymentTabActionIds[tab.key];
        if (!id || !canAccessPaymentTab(tab.key, routeAccess)) return [];
        return [
          {
            section: NAVIGATION_SECTION,
            id,
            name: `Go to ${tab.label}`,
            perform: () => navigate(tab.path),
          },
        ];
      }),
    );
  }

  return actions;
};
